import { ClassNames, keyframes } from '@emotion/react';
import { randomString } from '@luminovo/commons';
import { RefreshCwIcon } from 'lucide-react';
import * as React from 'react';
import type { JSX } from 'react';

import { colorSystem } from '../../theme';
import { createCustomIcon, type CustomIconProps } from './createCustomIcon';
import { ICON_MARKER } from './iconMarker';

/** The parametric search's spec letters, which Lucide has no icon for. */
const SPEC_GLYPH = { width: 16, height: 16, color: colorSystem.neutral[7] };

export const SpecCapacitanceIcon = createCustomIcon('SpecCapacitance', {
    ...SPEC_GLYPH,
    render: ({ color }) => (
        <path
            fill={color}
            d="M9.06 12.506c-.57 0-1.101-.103-1.596-.308a3.902 3.902 0 01-1.288-.924 4.617 4.617 0 01-.868-1.498C5.103 9.188 5 8.521 5 7.774c0-.999.182-1.853.546-2.562.364-.719.859-1.265 1.484-1.638C7.655 3.191 8.37 3 9.172 3c.56 0 1.06.117 1.498.35.439.224.798.485 1.078.784l-.784.882a2.564 2.564 0 00-.798-.588c-.29-.14-.62-.21-.994-.21-.541 0-1.017.145-1.428.434-.41.28-.733.681-.966 1.204-.224.523-.336 1.148-.336 1.876s.112 1.358.336 1.89c.233.532.555.943.966 1.232.42.29.905.434 1.456.434.401 0 .76-.084 1.078-.252.317-.177.611-.415.882-.714l.784.854c-.355.42-.77.747-1.246.98-.476.233-1.022.35-1.638.35z"
        />
    ),
});

export const SpecResistanceIcon = createCustomIcon('SpecResistance', {
    ...SPEC_GLYPH,
    render: ({ color }) => (
        <path
            fill={color}
            d="M5 12.17V3h2.954c.635 0 1.2.084 1.694.252.495.159.882.434 1.162.826.29.392.434.915.434 1.568 0 .635-.145 1.157-.434 1.568a2.55 2.55 0 01-1.162.924c-.495.205-1.06.308-1.694.308H6.4v3.724H5zm1.4-4.858h1.386c.672 0 1.185-.135 1.54-.406.355-.28.532-.7.532-1.26s-.177-.952-.532-1.176c-.355-.233-.868-.35-1.54-.35H6.4v3.192zm1.288.756l1.05-.798 2.828 4.9H9.984L7.688 8.068z"
        />
    ),
});

/** The T, which also stands for tolerance. */
export const SpecTemperatureCoefficientIcon = createCustomIcon('SpecTemperatureCoefficient', {
    ...SPEC_GLYPH,
    render: ({ color }) => <path fill={color} d="M6.954 12.17V4.176H4V3h7.308v1.176H8.354v7.994h-1.4z" />,
});

export const SpecPowerRatingIcon = createCustomIcon('SpecPowerRating', {
    ...SPEC_GLYPH,
    render: ({ color }) => (
        <path
            fill={color}
            d="M5 12.17V3h2.898c.672 0 1.265.089 1.778.266.523.168.929.457 1.218.868.29.401.434.943.434 1.624 0 .663-.15 1.209-.448 1.638-.29.43-.695.751-1.218.966-.513.205-1.101.308-1.764.308H6.4v3.5H5zm1.4-4.634h1.358c.747 0 1.297-.145 1.652-.434.355-.29.532-.737.532-1.344 0-.616-.182-1.04-.546-1.274-.364-.243-.91-.364-1.638-.364H6.4v3.416z"
        />
    ),
});

export const SpecVoltageIcon = createCustomIcon('SpecVoltage', {
    ...SPEC_GLYPH,
    render: ({ color }) => (
        <path
            fill={color}
            d="M6.898 12.17L4 3h1.484L6.87 7.774c.159.532.299 1.036.42 1.512.121.467.261.966.42 1.498h.056c.168-.532.313-1.031.434-1.498.13-.476.27-.98.42-1.512L9.992 3h1.428l-2.884 9.17H6.898z"
        />
    ),
});

export const SpecDielectricIcon = createCustomIcon('SpecDielectric', {
    ...SPEC_GLYPH,
    render: ({ color }) => (
        <path
            fill={color}
            d="M4.965 12V2.83h2.324c.915 0 1.69.173 2.324.518a3.407 3.407 0 011.47 1.54c.336.672.504 1.503.504 2.492 0 .99-.168 1.83-.504 2.52-.336.69-.817 1.213-1.442 1.568S8.255 12 7.359 12H4.965zm1.4-1.134h.84c.644 0 1.18-.13 1.61-.392.439-.27.77-.663.994-1.176.224-.513.336-1.153.336-1.918 0-.765-.112-1.4-.336-1.904-.224-.504-.555-.882-.994-1.134-.43-.252-.966-.378-1.61-.378h-.84v6.902z"
        />
    ),
});

export function IconNotFound(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 80, height: 80 }} fill="none" viewBox="0 0 32 32">
            <path
                fill={props.fill ?? colorSystem.neutral[8]}
                d="M27.14 14H9.25a.5.5 0 00-.48.37l-2 7.15-.72-12.31a.25.25 0 01.162-.26.27.27 0 01.108-.02H10a.63.63 0 01.5.25l1.22 1.57a.49.49 0 00.39.19H23v1.5a.5.5 0 001 0v-2a.5.5 0 00-.5-.51H12.3l-1.06-1.37A1.67 1.67 0 0010 7.92H6.32a1.31 1.31 0 00-.93.41 1.26 1.26 0 00-.34.94L6 24.5a.79.79 0 00.1.3.49.49 0 00.4.2h16.91a1.91 1.91 0 001.85-1.44l.74-2.89a.517.517 0 00-1-.26l-.76 2.9a.94.94 0 01-.89.69H7.16l2.47-9h16.86l-.68 2.58a.515.515 0 101 .25l.84-3.21a.5.5 0 00-.48-.63l-.03.01z"
            />
            <path
                fill={props.fill ?? colorSystem.neutral[8]}
                d="M16.148 21.395a.375.375 0 00-.11.114.263.263 0 00-.038.135c0 .047.013.093.038.135a.374.374 0 00.11.115c.045.033.1.06.16.078a.66.66 0 00.382 0 .52.52 0 00.16-.078 1.15 1.15 0 01.65-.19c.244 0 .478.069.65.19.046.033.1.06.16.078a.66.66 0 00.382 0 .52.52 0 00.16-.078.374.374 0 00.11-.115.263.263 0 00.038-.135.263.263 0 00-.038-.134.375.375 0 00-.11-.115 2.086 2.086 0 00-.62-.292A2.62 2.62 0 0017.5 21c-.251 0-.5.035-.732.103a2.086 2.086 0 00-.62.292zM14.983 18.184a1 1 0 11-1.966-.368l1.167-.799a1 1 0 01.799 1.167zM21.5 19.195a1 1 0 01-.537-1.927l1.232.695a1 1 0 01-.695 1.232z"
            />
        </svg>
    );
}

export function FrenchFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" transform="rotate(90 9 9)" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    <path fill="#D80027" d="M18 0v18h-5.871V0H18z" />
                    <path fill="#FCFCFD" d="M12.129 0v18H5.87V0h6.258z" />
                    <path fill="#0052B4" d="M5.871 0v18H0V0h5.871z" />
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" transform="rotate(90 9 9)" />
                </clipPath>
            </defs>
        </svg>
    );
}

export function SpanishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    <path fill="#D80027" d="M0 0h18v5.871H0V0z" />
                    <path fill="#FFDA44" d="M0 5.871h18v6.258H0V5.87z" />
                    <path fill="#D80027" d="M0 12.129h18V18H0v-5.871z" />
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

export function ItalianFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    <path fill="#D80027" d="M12 0v18h6V0h-6z" />
                    <path fill="#F0F0F0" d="M6 0v18h6V0H6z" />
                    <path fill="#006C35" d="M0 0v18h6V0H0z" />
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

export function GermanFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    <path fill="#000" d="M0 0h18v5.871H0V0z" />
                    <path fill="#D80027" d="M0 5.871h18v6.258H0V5.87z" />
                    <path fill="#FFDA44" d="M0 12.129h18V18H0v-5.871z" />
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

function CircularFlag({ children, ...props }: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>{children}</g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

export function ThaiFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect width="18" height="18" fill="#F4F5F8" />
            <rect y="0" width="18" height="3" fill="#A51931" />
            <rect y="6" width="18" height="6" fill="#2D2A4A" />
            <rect y="15" width="18" height="3" fill="#A51931" />
        </CircularFlag>
    );
}

export function JapaneseFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect width="18" height="18" fill="#F5F6FA" />
            <circle cx="9" cy="9" r="5" fill="#BC002D" />
        </CircularFlag>
    );
}

export function IndianFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect y="0" width="18" height="6" fill="#FF9933" />
            <rect y="6" width="18" height="6" fill="#F5F6FA" />
            <rect y="12" width="18" height="6" fill="#138808" />
            <circle cx="9" cy="9" r="2" fill="none" stroke="#000080" strokeWidth="0.6" />
        </CircularFlag>
    );
}

export function CzechFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect y="0" width="18" height="9" fill="#F5F6FA" />
            <rect y="9" width="18" height="9" fill="#D7141A" />
            <path d="M0 0L9 9L0 18Z" fill="#11457E" />
        </CircularFlag>
    );
}

export function DutchFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect y="0" width="18" height="6" fill="#AE1C28" />
            <rect y="6" width="18" height="6" fill="#F5F6FA" />
            <rect y="12" width="18" height="6" fill="#21468B" />
        </CircularFlag>
    );
}

export function HungarianFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect y="0" width="18" height="6" fill="#CD2A3E" />
            <rect y="6" width="18" height="6" fill="#F5F6FA" />
            <rect y="12" width="18" height="6" fill="#436F4D" />
        </CircularFlag>
    );
}

export function FinnishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect width="18" height="18" fill="#F5F6FA" />
            <rect x="5" y="0" width="3" height="18" fill="#003580" />
            <rect x="0" y="7.5" width="18" height="3" fill="#003580" />
        </CircularFlag>
    );
}

export function SwedishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect width="18" height="18" fill="#006AA7" />
            <rect x="5" y="0" width="3" height="18" fill="#FECC00" />
            <rect x="0" y="7.5" width="18" height="3" fill="#FECC00" />
        </CircularFlag>
    );
}

export function PolishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect y="0" width="18" height="9" fill="#F5F6FA" />
            <rect y="9" width="18" height="9" fill="#DC143C" />
        </CircularFlag>
    );
}

export function TurkishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <CircularFlag {...props}>
            <rect width="18" height="18" fill="#E30A17" />
            <circle cx="7.2" cy="9" r="3.6" fill="#F5F6FA" />
            <circle cx="8.4" cy="9" r="2.9" fill="#E30A17" />
            <path
                d="M11.4 7.4 L11.78 8.48 L12.92 8.51 L12.01 9.2 L12.34 10.29 L11.4 9.64 L10.46 10.29 L10.79 9.2 L9.88 8.51 L11.02 8.48 Z"
                fill="#F5F6FA"
            />
        </CircularFlag>
    );
}

// For languages where a national flag would be politically ambiguous, such as Chinese or Portuguese.
export function NeutralLanguageIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <circle cx="9" cy="9" r="8" fill={colorSystem.neutral[2]} stroke={colorSystem.neutral[6]} strokeWidth="1" />
            <ellipse cx="9" cy="9" rx="3.4" ry="8" stroke={colorSystem.neutral[6]} strokeWidth="0.8" />
            <line x1="1" y1="9" x2="17" y2="9" stroke={colorSystem.neutral[6]} strokeWidth="0.8" />
            <line x1="2.4" y1="5" x2="15.6" y2="5" stroke={colorSystem.neutral[6]} strokeWidth="0.8" />
            <line x1="2.4" y1="13" x2="15.6" y2="13" stroke={colorSystem.neutral[6]} strokeWidth="0.8" />
        </svg>
    );
}

const spinningIconRotationMillis = 1000;

// Emotion inserts a keyframes rule once per document, however many spinners use it, and keeps it.
const spin = keyframes({
    '0%': {
        transform: 'rotate(0deg)',
    },
    '100%': {
        transform: 'rotate(360deg)',
    },
});

// `cubic-bezier(0.4, 0, 0.2, 1)` is the theme's `transitions.easing.easeInOut`.
const spinning = {
    animation: `${spin} ${spinningIconRotationMillis}ms cubic-bezier(0.4, 0, 0.2, 1) 200ms infinite`,
};

/** RefreshCwIcon, turning clockwise, the way its arrows point. */
export const SpinningIcon = React.forwardRef<SVGSVGElement, CustomIconProps>(function SpinningIcon(
    { className, ...props },
    ref,
) {
    return (
        <ClassNames>
            {({ css, cx }) => <RefreshCwIcon ref={ref} className={cx(css(spinning), className)} {...props} />}
        </ClassNames>
    );
});

export const EmptySourcingScenariosIcon = (props: React.SVGProps<SVGSVGElement>): JSX.Element => {
    return (
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                d="M52.0859 46.334H48.7943L47.6276 45.209C52.6276 39.3757 55.2109 31.4173 53.7943 22.959C51.8359 11.3757 42.1693 2.12565 30.5026 0.708987C12.8776 -1.45768 -1.95573 13.3757 0.21094 31.0007C1.62761 42.6673 10.8776 52.334 22.4609 54.2923C30.9193 55.709 38.8776 53.1257 44.7109 48.1257L45.8359 49.2923V52.584L63.5443 70.2923C65.2526 72.0007 68.0443 72.0007 69.7526 70.2923C71.4609 68.584 71.4609 65.7923 69.7526 64.084L52.0859 46.334ZM27.0859 46.334C16.7109 46.334 8.33594 37.959 8.33594 27.584C8.33594 17.209 16.7109 8.83399 27.0859 8.83399C37.4609 8.83399 45.8359 17.209 45.8359 27.584C45.8359 37.959 37.4609 46.334 27.0859 46.334Z"
                fill="#D1D5E0"
            />
            <path
                d="M29.4744 17C31.2829 17 32.735 17.4956 33.8306 18.4868C34.9435 19.4607 35.5 20.8084 35.5 22.53C35.5 24.2342 34.9522 25.5385 33.8566 26.4427C32.7611 27.3296 31.309 27.7731 29.5005 27.7731L29.3961 29.7816H26.1877L26.0573 25.3472H27.2311C28.744 25.3472 29.9091 25.1472 30.7265 24.7472C31.5438 24.3473 31.9524 23.6169 31.9524 22.5561C31.9524 21.7909 31.7264 21.1823 31.2742 20.7301C30.8395 20.278 30.2395 20.0519 29.4744 20.0519C28.6744 20.0519 28.0484 20.2693 27.5963 20.7041C27.1615 21.1388 26.9441 21.7388 26.9441 22.5039H23.5009C23.4835 21.4431 23.7096 20.4954 24.1791 19.6607C24.6487 18.8259 25.3356 18.1738 26.2398 17.7043C27.1615 17.2348 28.2397 17 29.4744 17ZM27.7789 36.0159C27.118 36.0159 26.5703 35.8159 26.1355 35.416C25.7182 34.9986 25.5095 34.4856 25.5095 33.877C25.5095 33.2683 25.7182 32.764 26.1355 32.364C26.5703 31.9467 27.118 31.738 27.7789 31.738C28.4223 31.738 28.9527 31.9467 29.37 32.364C29.7874 32.764 29.9961 33.2683 29.9961 33.877C29.9961 34.4856 29.7874 34.9986 29.37 35.416C28.9527 35.8159 28.4223 36.0159 27.7789 36.0159Z"
                fill="#D1D5E0"
            />
        </svg>
    );
};

/** A status light in two shades of `tone`, `green` while monitoring is on. */
export const MonitoringIcon = createCustomIcon('Monitoring', {
    width: 16,
    height: 16,
    ownColors: true,
    props: { tone: 'neutral' as keyof typeof colorSystem },
    render: (_, { tone }) => (
        <>
            <rect x="2" y="2" width="12" height="12" rx="6" fill={colorSystem[tone][4]} />
            <rect x="5" y="5" width="6" height="6" rx="3" fill={colorSystem[tone][6]} />
        </>
    ),
});

/** A diamond bullet. */
export const DiamondIcon = createCustomIcon('Diamond', {
    width: 9,
    height: 8,
    render: ({ color }) => <rect y="4" width="6" height="6" rx="1" transform="rotate(-45 0 4)" fill={color} />,
});

export function HalfPortugalHalfBrazilFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <circle cx="9" cy="9" r="9" fill="#F5F6FA" />
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    {/* Top-left triangle - Portuguese flag */}
                    <g clipPath="polygon(0 0, 100% 0, 0 100%)">
                        <path fill="#D63030" d="M0 0h18v18H0z" />
                        <path fill="#1B806A" d="M0 0h6.4v18H0z" />
                        <circle cx="6.4" cy="5.5" r="1.7" fill="#F4C82E" />
                    </g>
                    {/* Bottom-right triangle - Brazilian flag */}
                    <g clipPath="polygon(100% 0, 100% 100%, 0 100%)">
                        <path fill="#4CAF50" d="M0 0h18v18H0z" />
                        <path fill="#F4C82E" d="M11.5 8l4.2 3.5-4.2 3.5-4.2-3.5z" />
                        <circle cx="11.5" cy="11.5" r="2" fill="#3838B5" />
                    </g>
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

export function HalfUSHalfBritishFlagIcon(props: React.SVGProps<SVGSVGElement>): JSX.Element {
    const clipPathId = randomString();
    const maskId = randomString();

    return (
        <svg
            {...ICON_MARKER}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 18 18"
            {...props}
        >
            <g clipPath={`url(#${clipPathId})`}>
                <circle cx="9" cy="9" r="9" fill="#F5F6FA" />
                <mask
                    id={maskId}
                    style={{ maskType: 'alpha' }}
                    width="18"
                    height="18"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                >
                    <circle cx="9" cy="9" r="9" fill="#fff" />
                </mask>
                <g mask={`url(#${maskId})`}>
                    {/* Left half - British Flag */}
                    <g clipPath="inset(0 50% 0 0)">
                        <path fill="#0052B4" d="M4.975 6.645H-.703V.967l5.678 5.678z" />
                        <path fill="#D80027" d="M-.72-.72l7.365 7.365H5.52L2.092 3.217-.721-.721z" />
                        <path
                            fill="#0052B4"
                            d="M6.645-.826v5.8l-3.13-3.11-1.81-2.69h4.94zM17.156-.826l-5.8 5.8v-5.8h5.8z"
                        />
                        <path fill="#D80027" d="M18.826-.826l-7.47 7.47V5.538l3.462-3.463 4.008-2.9z" />
                        <path fill="#0052B4" d="M18.984 6.645h-5.959l3.112-3.13L18.984.423v6.223z" />
                        <path
                            fill="#D80027"
                            d="M-.457 7.787v2.39h8.262v8.403h2.39v-8.402h8.297v-2.39h-8.297V-.51h-2.39v8.297H-.457z"
                        />
                        <path fill="#0052B4" d="M18.791 11.338h-5.766l3.094 3.146 2.672 1.213v-4.36z" />
                        <path fill="#D80027" d="M18.756 17.63l-6.293-6.292H11.32l4.043 4.043 3.393 2.25z" />
                        <path
                            fill="#0052B4"
                            d="M16.84 18.51l-5.485-5.485v5.485h5.485zM1.02 18.65l5.625-5.625v5.625H1.02z"
                        />
                        <path fill="#D80027" d="M6.645 12.463v-1.107l-4.008 4.007-2.233 3.34 6.24-6.24z" />
                        <path fill="#0052B4" d="M-.404 16.734l5.396-5.396H-.404v5.396z" />
                    </g>
                    {/* Right half - US Flag */}
                    <g transform="translate(9 0)">
                        {/* Red and white stripes */}
                        <path
                            fill="#D80027"
                            d="M0 0h18v1.385H0zM0 2.77h18v1.385H0zM0 5.54h18v1.385H0zM0 8.31h18v1.385H0zM0 11.08h18v1.385H0zM0 13.85h18v1.385H0zM0 16.62h18V18H0z"
                        />
                        {/* Blue canton */}
                        <path fill="#0052B4" d="M0 0h7.2v7.7H0z" />
                        {/* Stars - simplified representation for small size */}
                        <path
                            fill="#F5F6FA"
                            d="M.6 .8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM2.4 .8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM4.2 .8l.2.6H5l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM6 .8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM1.5 2.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM3.3 2.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM5.1 2.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM.6 3.8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM2.4 3.8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM4.2 3.8l.2.6H5l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM6 3.8l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM1.5 5.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM3.3 5.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6zM5.1 5.3l.2.6h.6l-.5.4.2.6-.5-.4-.5.4.2-.6-.5-.4h.6z"
                        />
                    </g>
                </g>
            </g>
            <defs>
                <clipPath id={clipPathId}>
                    <path fill="#fff" d="M0 0H18V18H0z" />
                </clipPath>
            </defs>
        </svg>
    );
}

export const MousePointerIcon = (props: React.SVGProps<SVGSVGElement>): JSX.Element => {
    return (
        <svg width="73" height="49" viewBox="0 0 73 49" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <rect x="0.5" y="0.5" width="62" height="38" rx="1.5" fill="#E8EDFF" stroke="#B8C4FF" />
            <path
                d="M56.602 31.5049C56.8117 31.4811 57.024 31.5117 57.2197 31.5928L70.5538 37.0087H70.5547C70.7675 37.0954 70.9517 37.2392 71.0877 37.4229L71.143 37.5043L71.1919 37.5889C71.2975 37.7917 71.3455 38.0204 71.3302 38.2497C71.3128 38.5118 71.2129 38.762 71.0454 38.9643C70.8779 39.1664 70.6508 39.3109 70.3968 39.3769H70.396L65.2926 40.6936H65.2918C65.1477 40.7306 65.0163 40.8058 64.9109 40.9109C64.8056 41.016 64.7302 41.1476 64.6928 41.2917L64.692 41.2909L63.3769 46.3951V46.3967C63.311 46.6508 63.1664 46.8778 62.9643 47.0453C62.7621 47.2128 62.5118 47.3127 62.2498 47.3301C61.9879 47.3476 61.727 47.2821 61.5044 47.143C61.2817 47.0037 61.1078 46.7978 61.0088 46.5546V46.5538L55.5921 33.2204L55.5929 33.2196C55.4998 32.9951 55.4733 32.7482 55.5188 32.5092L55.54 32.418C55.5966 32.2082 55.707 32.0159 55.8615 31.8614L55.9306 31.7979C56.0954 31.6563 56.2948 31.5597 56.5092 31.5188L56.602 31.5049ZM62.0773 44.7577L63.0791 40.8742V40.8734C63.1912 40.4412 63.4173 40.0463 63.7334 39.7309C64.0492 39.4157 64.4438 39.1911 64.8759 39.0798L68.7578 38.078L57.507 33.5069L62.0773 44.7577Z"
                fill="#4B54B0"
            />
        </svg>
    );
};
